import { quantile } from "@/utils/statistics";
import { close, fillHoles, forEachComponent, GridMask } from "./grid-mask";
import {
  createGrid,
  findLayers,
  Grid,
  groupHeights,
  markHorizontal,
} from "./height-grid";
import { findLowestSurface, growToWalls } from "./lowest-level";
import { findUpperLevels } from "./upper-levels";

/** The area of a level seen from above, on a grid aligned with the x and z axes */
export type LevelFootprint = GridMask &
  Pick<Grid, "cellSize" | "minX" | "minZ">;

/** A walkable level and its area seen from above */
export type PlanLevel = {
  /** Typical height of the level, in meters; the lowest level spans all the ground and floor heights */
  elevation: number;
  areaM2: number;
  footprint: LevelFootprint;
  /** Height of the surface in each cell of the footprint, NaN outside */
  heights: Float32Array;
};

/** The plan area: every walkable level seen from above, indoors and outdoors, without roofs */
export type PlanArea = {
  /** The lowest surface everywhere first, then the upper levels from the lowest */
  levels: PlanLevel[];
  totalAreaM2: number;
};

export type PlanAreaOptions = {
  /** Size of a grid cell, in meters, increased for very large point clouds */
  cellSize?: number;
};

/** Finer cells break up the surfaces of the point samples the app downloads */
const DEFAULT_CELL_SIZE = 0.2;
/** Unscanned floor narrower than about twice this size is filled, e.g. under tables */
const GAP_RADIUS = 0.4;
/** Enclosed unscanned areas up to this size are filled, larger ones are voids, e.g. unscanned cores */
const MAX_FILLED_HOLE_M2 = 10;
/** Smaller parts are clutter, e.g. tops of furniture or cars */
const MIN_PART_M2 = 20;

/**
 * @param grid The grid
 * @param heights Height of a level in each cell, NaN outside
 * @returns the cells of the level, without small gaps, small holes and small parts
 */
function toFootprint(grid: Grid, heights: Float32Array): LevelFootprint {
  const { width, height, cellSize, minX, minZ } = grid;
  const cellArea = cellSize ** 2;
  const mask = {
    width,
    height,
    data: Uint8Array.from(heights, (value) => (Number.isNaN(value) ? 0 : 1)),
  };
  const filled = fillHoles(
    close(mask, Math.max(1, Math.round(GAP_RADIUS / cellSize))),
    MAX_FILLED_HOLE_M2 / cellArea,
  );
  const data = new Uint8Array(filled.data.length);
  forEachComponent(filled, 1, (cells) => {
    if (cells.length * cellArea < MIN_PART_M2) return;
    for (const cell of cells) data[cell] = 1;
  });
  return { width, height, data, cellSize, minX, minZ };
}

/**
 * Estimates the plan area of a point cloud: the sum over every walkable level of its area seen from above,
 * indoors and outdoors, counting floors under furniture and equipment, but not roofs, tree tops and equipment tops.
 * A double height space counts once. Scanner positions are not needed, as merged point clouds have none.
 *
 * @param points Point positions as x, y, z triplets, y up, in meters, ideally sampled evenly
 * @param options How to estimate
 * @returns the area of each level and the total
 */
export function estimatePlanArea(
  points: Float32Array,
  { cellSize = DEFAULT_CELL_SIZE }: PlanAreaOptions = {},
): PlanArea {
  if (!points.length) return { levels: [], totalAreaM2: 0 };
  const grid = createGrid(points, cellSize);
  const cells = groupHeights(points, grid);
  const layers = findLayers(cells);
  const isHorizontal = markHorizontal(grid, layers);
  const lowest = findLowestSurface(grid, layers, isHorizontal);
  const upperLevels = findUpperLevels(
    grid,
    cells,
    layers,
    isHorizontal,
    lowest,
  );
  // Only the lowest level grows, upper levels would grow into the ceilings around stair voids
  growToWalls(grid, cells, lowest);

  const lowestHeights = lowest.filter((value) => !Number.isNaN(value)).sort();
  const levels = [
    {
      elevation: lowestHeights.length ? quantile(lowestHeights, 0.5) : 0,
      heights: lowest,
    },
    ...upperLevels,
  ].flatMap(({ elevation, heights }) => {
    const footprint = toFootprint(grid, heights);
    const cellCount = footprint.data.reduce((sum, value) => sum + value, 0);
    if (!cellCount) return [];
    const surface = Float32Array.from(footprint.data, (isInside, cell) => {
      if (!isInside) return NaN;
      // The gaps filled in the footprint have no points, they lie at the typical height
      return Number.isNaN(heights[cell]) ? elevation : heights[cell];
    });
    return {
      elevation,
      areaM2: cellCount * grid.cellSize ** 2,
      footprint,
      heights: surface,
    };
  });
  return {
    levels,
    totalAreaM2: levels.reduce((sum, level) => sum + level.areaM2, 0),
  };
}
